import { useMemo } from 'react'
import { problemTrackerWorkflowConfigFactory } from './problemTrackerWorkflowConfigFactory'
import { generalWorkflowConfigFactory } from './generalWorkflowConfigFactory'
import { automatedFixWorkflowConfigFactory } from './automatedFixWorkflowConfigFactory'
import { measureAndDetectWorkflowConfigFactory } from './measureAndDetectWorkflowConfigFactory.ts'

export interface WorkflowColumnConfiguration {
  isSuggestible?: boolean
  isFlaggable?: boolean
  isVisible?: boolean
  isFreezable?: boolean
  ordering?: number
  componentTypeLayers?: string[]
  showComponentTypeLabels?: boolean
}

export interface WorkflowSizeModalConfiguration {
  preFill?: boolean
  sizeUomPath: {
    type: string
    name: string
  }
  sizeValuePath: {
    type: string
    name: string
  }
  sizePath: {
    type: string
    name: string
  }
  unitValuePath?: {
    type: string
    name: string
  }
  unitUomPath?: {
    type: string
    name: string
  }
  unitCountPath?: {
    type: string
    name: string
  }
}

export interface WorkflowConfiguration {
  name: string
  showCombineButton: boolean
  showClassifiedType: boolean
  showLocaleId: boolean
  showDueDate: boolean
  showSizeModal?: boolean
  sizeModalConfigs?: WorkflowSizeModalConfiguration
  sizeFlagChangeCombined: boolean
  displayProductIdOnly?: boolean
  displayRetailerProductInformation?: boolean
  showReviewerAndAuditorDropdown?: boolean
  getColumnConfiguration: (
    componentType: string | null,
    componentName: string
  ) => WorkflowColumnConfiguration | null
}

export interface WorkflowConfigurationFactory {
  regex: string
  priority: number
  build: (workflowId, allowSuggestedValues) => WorkflowConfiguration
}

const additionalWorkflowConfigFactories = [
  problemTrackerWorkflowConfigFactory,
  automatedFixWorkflowConfigFactory,
  measureAndDetectWorkflowConfigFactory,
]

export const useGetWorkflowConfig = (workflowId: string, allowSuggestedValues: boolean) =>
  useMemo(
    () => getWorkflowConfig(workflowId, allowSuggestedValues),
    [workflowId, allowSuggestedValues]
  )

export const getWorkflowConfig = (
  workflowId: string,
  allowSuggestedValues: boolean
): WorkflowConfiguration => {
  let winningConfiguration = generalWorkflowConfigFactory
  for (const workflowConfigFactory of additionalWorkflowConfigFactories) {
    const regex = new RegExp(workflowConfigFactory.regex)
    const isMatching = regex.test(workflowId)
    if (isMatching && workflowConfigFactory.priority > winningConfiguration.priority) {
      winningConfiguration = workflowConfigFactory
    }
  }
  return winningConfiguration.build(workflowId, allowSuggestedValues)
}
